import { useMutation } from '@tanstack/react-query';
import type { App } from '@hangar/shared';
import { toast } from 'sonner';
import { downloadCliPackage } from '@/api/endpointsCli';
import { useEasSetup } from '@/api/hooksEas';
import { useProfiles } from '@/api/hooksEnv';
import { saveBlob } from '@/lib/download';
import { showMutationError } from '@/lib/mutationError';
import { CopyButton } from '@/components/CopyButton';
import { ErrorState } from '@/components/ErrorState';
import { Icon } from '@/components/Icon';
import { Tag } from '@/components/badges';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { appJsonSnippet } from '@/features/apps/easSetupModel';
import { bundleFilename, bundleText, fileKey, repoFiles, type RepoFile } from './repoFilesModel';

export interface RepoFilesSheetProps {
  app: App;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * The files an Expo / React Native app's folder needs, filled for this app (SPEC decision 29, plan P31): one card per
 * file with its own Copy (the Hangar CLI package: Download), and "Download all" as one Markdown file. Read-only: Hangar
 * never writes to the repository.
 */
export function RepoFilesSheet({ app, open, onOpenChange }: RepoFilesSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" data-testid="repo-files-sheet" className="w-full gap-0 p-0 sm:max-w-2xl" aria-describedby="repo-files-desc">
        {open ? <SheetBody app={app} onClose={() => onOpenChange(false)} /> : null}
      </SheetContent>
    </Sheet>
  );
}

/** The app's build setup and build profiles, turned into the files; loading and error states of their own. */
function useRepoFiles(app: App) {
  const setup = useEasSetup(app.id);
  const profiles = useProfiles(app.id);
  const loading = (setup.isLoading && !setup.data) || (profiles.isLoading && !profiles.data);
  const failed = [profiles, setup].find((q) => q.isError && !q.data);
  const files = loading || failed ? null : repoFiles({ app, appJson: setup.data ? appJsonSnippet(setup.data) : null, profiles: profiles.data ?? [] });
  return { files, loading, failed };
}

function SheetBody({ app, onClose }: { app: App; onClose: () => void }) {
  const { files, loading, failed } = useRepoFiles(app);
  const download = () => {
    if (!files) return;
    const name = bundleFilename(app.slug);
    saveBlob(new Blob([bundleText(app, files)], { type: 'text/markdown;charset=utf-8' }), name);
    toast.success(`Downloaded ${name}`);
  };
  return (
    <div className="flex h-full min-h-0 flex-col">
      <SheetHeader className="border-b">
        <SheetTitle className="truncate">Repository files for {app.name}</SheetTitle>
        <SheetDescription id="repo-files-desc" className="text-xs">
          Filled for this app. Hangar never writes to your repository: add them, commit and push, then run a build.
        </SheetDescription>
      </SheetHeader>
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 sm:p-5" data-testid="repo-files-list">
        {loading ? <FilesSkeleton /> : null}
        {failed ? <ErrorState compact error={failed.error} onRetry={() => void failed.refetch()} title="Couldn't prepare the files" /> : null}
        {files?.map((f) => <FileCard key={f.id} file={f} />)}
      </div>
      <SheetFooter className="flex-row justify-end gap-2 border-t">
        <Button type="button" variant="ghost" onClick={onClose}>
          Close
        </Button>
        <Button type="button" data-testid="repo-files-download" disabled={!files} onClick={download}>
          <Icon name="download" size={18} />
          Download all
        </Button>
      </SheetFooter>
    </div>
  );
}

function FileCard({ file }: { file: RepoFile }) {
  const key = fileKey(file.id);
  return (
    <section data-testid={`repo-file-${key}`} aria-label={file.path} className="min-w-0 rounded-lg border bg-card shadow-xs">
      <header className="flex min-h-12 items-center gap-2 border-b px-3 py-2">
        <Icon name="description" size={18} className="shrink-0 text-subtle" />
        <span className="min-w-0 truncate font-mono text-[13px] font-medium text-foreground" title={file.path}>
          {file.path}
        </span>
        <Tag className="shrink-0">{file.action === 'merge' ? 'merge' : 'add'}</Tag>
        {file.download ? <CliDownloadButton testId={`repo-file-download-${key}`} /> : null}
        {file.content === null ? null : <CopyButton text={file.content} label="Copy" testId={`repo-file-copy-${key}`} className="ml-auto" />}
      </header>
      <p className="px-3 pt-2 text-[13px] leading-relaxed text-muted-foreground">{file.note}</p>
      {file.content === null ? <div className="h-3" /> : <pre className="m-3 mt-2 overflow-x-auto rounded-md border bg-background p-3 font-mono text-[12px] leading-5 text-foreground">{file.content}</pre>}
    </section>
  );
}

/** Saves the Hangar CLI tarball as hangar-cli.tgz (`GET /v1/cli/package`); a missing package says so in a toast. */
function CliDownloadButton({ testId }: { testId: string }) {
  const download = useMutation({ mutationFn: downloadCliPackage });
  const run = () =>
    download.mutate(undefined, {
      onSuccess: (blob) => {
        saveBlob(blob, 'hangar-cli.tgz');
        toast.success('Downloaded hangar-cli.tgz', { description: 'Commit it at the path shown on its card.' });
      },
      onError: (e) => showMutationError(e, 'Could not download the Hangar CLI'),
    });
  return (
    <Button type="button" variant="outline" size="sm" className="ml-auto shrink-0" data-testid={testId} loading={download.isPending} onClick={run}>
      <Icon name="download" size={16} />
      Download
    </Button>
  );
}

function FilesSkeleton() {
  return (
    <div className="flex flex-col gap-4" aria-busy="true" data-testid="repo-files-loading">
      {[0, 1, 2].map((i) => (
        <div key={i} className="rounded-lg border">
          <div className="flex h-12 items-center gap-2 border-b px-3">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="ml-auto h-8 w-20" />
          </div>
          <Skeleton className="m-3 h-24" />
        </div>
      ))}
    </div>
  );
}
